import { db } from '../../firebase/firebaseConfig';
import {
  doc,
  updateDoc,
  getDoc,
  setDoc,
  collection,
  query,
  where,
  getDocs,
  writeBatch
} from 'firebase/firestore';
import {
  MASTERY_LEVELS,
  BASE_MASTERY,
  calculateMastery as calcMastery,
  calculateNextReview as calcNextReview,
  getMasteryLevel as getLevel,
  isCardMastered,
  isSuccessfulRating,
  createInitialFsrsState,
  migrateToFsrsState,
  normalizeRating
} from './algorithm';
import { isSrsProgressRowDue } from '../../services/reviewQueueService';
import { notifySnippetMasteryCountChanged } from '../../utils/snippetMasteryCountEvents';

function initStabilityForNewCard() {
  return createInitialFsrsState().stability;
}

function initDifficultyForNewCard() {
  return createInitialFsrsState().difficulty;
}

class SpacedRepetition {
  constructor(userId) {
    this.userId = userId;
  }

  calculateMastery(currentMastery, wasSuccessful, streak, lastReviewed) {
    return calcMastery(currentMastery, wasSuccessful, streak, lastReviewed);
  }

  calculateNextReview(currentData, ratingInput, now = new Date(), options = {}) {
    return calcNextReview(currentData, ratingInput, now, options);
  }

  getMasteryLevel(mastery) {
    return getLevel(mastery);
  }

  /**
   * Record first-exposure orientation without an SRS review (totalReviews stays 0).
   * The next Check or Again commits a real FSRS grade.
   *
   * @param {string} deckId
   * @param {string} cardId
   */
  async recordFirstExposureOrientation(deckId, cardId) {
    try {
      const progressRef = doc(db, `users/${this.userId}/progress/${deckId}_${cardId}`);
      const progressDoc = await getDoc(progressRef);
      const nowIso = new Date().toISOString();

      if (progressDoc.exists() && progressDoc.data().orientationCompleted === true) {
        return { ...progressDoc.data(), deckId, cardId, orientationCompleted: true };
      }

      const orientationFields = {
        deckId,
        cardId,
        orientationCompleted: true,
        orientationCompletedAt: nowIso,
        lastReviewed: nowIso
      };

      if (progressDoc.exists()) {
        await updateDoc(progressRef, orientationFields);
        return { ...progressDoc.data(), ...orientationFields };
      }

      const initialData = {
        level: 0,
        mastery: BASE_MASTERY.INITIAL,
        streak: 0,
        totalReviews: 0,
        successfulReviews: 0,
        repetitions: 0,
        easiness: 2.5,
        stability: initStabilityForNewCard(),
        difficulty: initDifficultyForNewCard(),
        interval: 0,
        cardPhase: 'new',
        learningStep: 0,
        intervalMinutes: null,
        ...orientationFields
      };
      await setDoc(progressRef, initialData);
      return initialData;
    } catch (error) {
      console.error('Error recording first-exposure orientation:', error);
      throw error;
    }
  }

  /**
   * @param {string} deckId
   * @param {string} cardId
   * @param {boolean | string | { rating: string, accuracy?: number, initiallyWrongCount?: number, totalLines?: number }} ratingInput
   */
  async updateCardProgress(deckId, cardId, ratingInput) {
    try {
      const rating = normalizeRating(ratingInput);
      const reviewMetrics = ratingInput && typeof ratingInput === 'object' && !Array.isArray(ratingInput)
        ? ratingInput
        : {};
      const sameSessionContinue = reviewMetrics.sameSessionContinue === true;
      console.log('updateCardProgress called with:', {
        deckId,
        cardId,
        rating,
        userId: this.userId
      });
      const progressRef = doc(db, `users/${this.userId}/progress/${deckId}_${cardId}`);
      const progressDoc = await getDoc(progressRef);

      let currentData = {
        level: 0,
        mastery: BASE_MASTERY.INITIAL,
        streak: 0,
        totalReviews: 0,
        successfulReviews: 0,
        repetitions: 0,
        easiness: 2.5,
        stability: initStabilityForNewCard(),
        difficulty: initDifficultyForNewCard(),
        interval: 0,
        cardPhase: 'new',
        learningStep: 0,
        intervalMinutes: null,
        lastReviewed: new Date().toISOString()
      };

      if (progressDoc.exists()) {
        currentData = { ...currentData, ...progressDoc.data() };
        const migrated = migrateToFsrsState(currentData);
        currentData.stability = migrated.stability;
        currentData.difficulty = migrated.difficulty;
        console.log('Existing progress data:', currentData);
      } else {
        console.log('No existing progress data, using defaults');
      }

      const wasSuccessful = isSuccessfulRating(rating);

      // Update streak
      const streak = wasSuccessful ? currentData.streak + 1 : 0;

      // Calculate new mastery
      const mastery = this.calculateMastery(
        currentData.mastery,
        rating,
        streak,
        currentData.lastReviewed
      );

      const {
        stability,
        difficulty,
        repetitions,
        easiness,
        interval,
        intervalMinutes,
        cardPhase,
        learningStep,
        lastRating,
        nextReview
      } = this.calculateNextReview(currentData, rating, new Date(), {
        cardKey: `${deckId}_${cardId}`,
        sameSessionContinue
      });

      const totalReviews = currentData.totalReviews + 1;
      const successfulReviews = currentData.successfulReviews + (wasSuccessful ? 1 : 0);
      const nowIso = new Date().toISOString();
      const firstReviewed =
        currentData.firstReviewed || currentData.lastReviewed || nowIso;
      const wasMasteredBefore =
        currentData.is_mastered === true ||
        isCardMastered({
          ...currentData,
          mastery: currentData.mastery,
          totalReviews: currentData.totalReviews
        });
      const is_mastered = isCardMastered({
        mastery,
        totalReviews,
        repetitions,
        interval,
        firstReviewed,
        lastReviewed: nowIso
      });
      const newlyMastered = is_mastered && !wasMasteredBefore;

      const updatedData = {
        deckId,
        cardId,
        mastery,
        streak,
        totalReviews,
        successfulReviews,
        stability,
        difficulty,
        repetitions,
        easiness,
        interval,
        intervalMinutes,
        cardPhase,
        learningStep,
        lastRating,
        lastAccuracy: reviewMetrics.accuracy,
        lastInitiallyWrongCount: reviewMetrics.initiallyWrongCount,
        lastTotalLines: reviewMetrics.totalLines,
        nextReview,
        firstReviewed,
        lastReviewed: nowIso,
        is_mastered
      };

      console.log('Saving updated progress data:', updatedData);
      const batch = writeBatch(db);
      batch.set(progressRef, updatedData);
      const cardRef = doc(db, `users/${this.userId}/decks/${deckId}/cards/${cardId}`);
      batch.set(cardRef, { is_mastered }, { merge: true });
      await batch.commit();

      if (newlyMastered) {
        notifySnippetMasteryCountChanged(1);
      }

      return {
        ...updatedData,
        masteryLevel: this.getMasteryLevel(mastery),
        newlyMastered
      };
    } catch (error) {
      console.error('Error updating card progress:', error);
      throw error;
    }
  }

  /**
   * Legacy SnippetManager / users/{uid}/snippet_progress is removed — no Firestore reads or writes.
   * Returns a neutral shape so callers that still branch on snippet APIs do not throw.
   */
  async updateSnippetProgress(snippetId, wasSuccessful, options = {}) {
    const mastery = BASE_MASTERY.INITIAL;
    return {
      snippetId,
      mastery,
      streak: 0,
      totalReviews: 0,
      successfulReviews: 0,
      repetitions: 0,
      easiness: 2.5,
      stability: initStabilityForNewCard(),
      difficulty: initDifficultyForNewCard(),
      interval: 0,
      nextReview: null,
      lastReviewed: new Date().toISOString(),
      masteryLevel: this.getMasteryLevel(mastery)
    };
  }

  /**
   * Single fetch of all card progress (for dashboard / batch use).
   * @returns {Promise<Array<{ id: string } & Record<string, unknown>>>}
   */
  async fetchAllCardProgressRows() {
    if (this.userId == null || this.userId === '') {
      return [];
    }
    const snap = await getDocs(collection(db, `users/${this.userId}/progress`));
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  }

  /**
   * Same rules as getDueCards, but in-memory for one deck's progress rows.
   */
  filterDueCardProgressList(progressList, now = new Date()) {
    return progressList
      .map((item) => ({
        ...item,
        masteryLevel: this.getMasteryLevel(item.mastery || 0)
      }))
      .filter((card) => isSrsProgressRowDue(card, now))
      .sort((a, b) => {
        if (a.mastery !== b.mastery) return a.mastery - b.mastery;
        return new Date(a.lastReviewed || 0) - new Date(b.lastReviewed || 0);
      });
  }

  /**
   * Build due-deck list from an in-memory progress snapshot (avoids N deck queries).
   * @param {Array<{ id: string }>} decks - Deck docs from users/{uid}/decks
   * @param {Array<{ deckId?: string }>} progressRows - From fetchAllCardProgressRows
   */
  buildDueDecksFromProgress(decks, progressRows) {
    const now = new Date();
    const byDeck = new Map();
    for (const row of progressRows) {
      const did = row.deckId;
      if (!did) continue;
      if (!byDeck.has(did)) byDeck.set(did, []);
      byDeck.get(did).push(row);
    }

    const out = [];
    for (const deck of decks) {
      const list = byDeck.get(deck.id) || [];
      const dueCards = this.filterDueCardProgressList(list, now);
      if (dueCards.length > 0) {
        out.push({
          ...deck,
          dueCards: dueCards.length,
          nextReview: dueCards[0].nextReview,
          tags: deck.tags || []
        });
      }
    }
    out.sort((a, b) => new Date(a.nextReview) - new Date(b.nextReview));
    return out;
  }

  /**
   * Upcoming reviews in the next `days` (titles filled separately).
   */
  buildUpcomingReviewsFromProgress(progressRows, days = 7) {
    const now = new Date();
    const futureDate = new Date(now);
    futureDate.setDate(futureDate.getDate() + days);

    const isUpcoming = (item) => {
      if (!item?.nextReview) return false;
      if (isSrsProgressRowDue(item, now)) return false;
      const reviewDate = new Date(item.nextReview);
      return reviewDate <= futureDate;
    };

    return progressRows
      .filter((item) => isUpcoming(item))
      .map((item) => ({
        id: item.id,
        ...item,
        sourceType: 'card',
        masteryLevel: this.getMasteryLevel(item.mastery || 0),
        dueDate: item.nextReview ? new Date(item.nextReview) : null
      }))
      .sort((a, b) => new Date(a.nextReview) - new Date(b.nextReview));
  }

  /**
   * Overall stats from in-memory progress (same shape as getOverallStats).
   */
  buildOverallStatsFromProgress(progressRows) {
    const now = new Date();
    let totalCards = 0;
    let totalCardMastery = 0;
    let cardsDueToday = 0;
    let cardsReviewedToday = 0;
    let cardStreak = 0;
    let cardLongestStreak = 0;

    for (const data of progressRows) {
      totalCards++;
      totalCardMastery += data.mastery || 0;

      if (data.nextReview) {
        if (isSrsProgressRowDue(data, now)) {
          cardsDueToday++;
        }
      }

      if (data.lastReviewed) {
        const lastReviewDate = new Date(data.lastReviewed);
        if (
          lastReviewDate.getDate() === now.getDate() &&
          lastReviewDate.getMonth() === now.getMonth() &&
          lastReviewDate.getFullYear() === now.getFullYear()
        ) {
          cardsReviewedToday++;
        }
      }

      cardStreak = Math.max(cardStreak, data.streak || 0);
      cardLongestStreak = Math.max(cardLongestStreak, data.longestStreak || 0);
    }

    return {
      totalItems: totalCards,
      masteryRate: totalCards > 0 ? Math.round(totalCardMastery / totalCards) : 0,
      dueToday: cardsDueToday,
      reviewedToday: cardsReviewedToday,
      currentStreak: cardStreak,
      longestStreak: cardLongestStreak
    };
  }

  // Get due cards
  async getDueCards(deckId) {
    try {
      const rows = await this.getDeckCardProgressList(deckId);
      return this.filterDueCardProgressList(rows, new Date());
    } catch (error) {
      console.error('Error getting due cards:', error);
      throw error;
    }
  }

  /**
   * All SRS progress rows for a deck (due and not-yet-due).
   */
  async getDeckCardProgressList(deckId) {
    try {
      this.debug('Getting card progress for deck:', deckId);
      const progressRef = collection(db, `users/${this.userId}/progress`);
      const q = query(progressRef, where('deckId', '==', deckId));
      const snapshot = await getDocs(q);

      this.debug('Found card progress records for deck:', snapshot.docs.length);

      return snapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data()
      }));
    } catch (error) {
      console.error('Error getting deck card progress:', error);
      throw error;
    }
  }

  // Debug logging helper - only logs in development mode
  debug(...args) {
    if (process.env.NODE_ENV !== 'production') {
      console.log(...args);
    }
  }

  /**
   * Legacy standalone user snippets (SnippetManager) are no longer supported.
   * Due items are cookbook cards only; use getDueDecks / getDueCards.
   */
  async getDueSnippets() {
    return [];
  }

  // Get card statistics
  async getCardStats(cardId) {
    try {
      const progressRef = collection(db, `users/${this.userId}/progress`);
      const q = query(progressRef, where('cardId', '==', cardId));
      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        return {
          mastery: BASE_MASTERY.INITIAL,
          masteryLevel: MASTERY_LEVELS.BEGINNER.name,
          streak: 0,
          totalReviews: 0,
          successRate: 0
        };
      }

      const data = snapshot.docs[0].data();
      const successRate = data.totalReviews > 0
        ? (data.successfulReviews / data.totalReviews) * 100
        : 0;

      return {
        mastery: data.mastery || BASE_MASTERY.INITIAL,
        masteryLevel: this.getMasteryLevel(data.mastery || BASE_MASTERY.INITIAL),
        streak: data.streak || 0,
        totalReviews: data.totalReviews || 0,
        successRate: Math.round(successRate)
      };
    } catch (error) {
      console.error('Error getting card stats:', error);
      throw error;
    }
  }

  /** Legacy snippet library stats — no Firestore; use getCardStats for deck cards. */
  async getSnippetStats(_snippetId) {
    return {
      mastery: BASE_MASTERY.INITIAL,
      masteryLevel: MASTERY_LEVELS.BEGINNER.name,
      streak: 0,
      totalReviews: 0,
      successRate: 0
    };
  }

  // Get upcoming reviews for the next N days (cookbook cards only; legacy user snippets removed)
  async getUpcomingReviews(days = 7) {
    try {
      const progressRows = await this.fetchAllCardProgressRows();
      const upcomingCards = this.buildUpcomingReviewsFromProgress(progressRows, days);

      const cardsWithDetails = await Promise.all(
        upcomingCards.map(async (progress) => {
          const cardRef = doc(
            db,
            `users/${this.userId}/decks/${progress.deckId}/cards/${progress.cardId}`
          );
          const cardDoc = await getDoc(cardRef);
          if (cardDoc.exists()) {
            return {
              ...progress,
              title: cardDoc.data().title || `Card ${progress.cardId}`,
              masteryLevel: this.getMasteryLevel(progress.mastery || 0),
              dueDate: new Date(progress.nextReview)
            };
          }
          return {
            ...progress,
            title: `Card ${progress.cardId}`,
            masteryLevel: this.getMasteryLevel(progress.mastery || 0)
          };
        })
      );

      return cardsWithDetails.sort(
        (a, b) => new Date(a.nextReview) - new Date(b.nextReview)
      );
    } catch (error) {
      console.error('Error getting upcoming reviews:', error);
      throw error;
    }
  }

  // Get overall SRS statistics (cookbook cards only; legacy snippet_progress excluded)
  async getOverallStats() {
    try {
      const progressRows = await this.fetchAllCardProgressRows();
      return this.buildOverallStatsFromProgress(progressRows);
    } catch (error) {
      console.error('Error getting overall stats:', error);
      throw error;
    }
  }

  // Reset card progress
  async resetCardProgress(deckId, cardId) {
    try {
      const progressRef = doc(db, `users/${this.userId}/progress/${deckId}_${cardId}`);
      const resetPayload = {
        deckId,
        cardId,
        mastery: BASE_MASTERY.INITIAL,
        masteryLevel: MASTERY_LEVELS.BEGINNER.name,
        streak: 0,
        totalReviews: 0,
        successfulReviews: 0,
        repetitions: 0,
        easiness: 2.5,
        stability: initStabilityForNewCard(),
        difficulty: initDifficultyForNewCard(),
        interval: 0,
        cardPhase: 'new',
        learningStep: 0,
        intervalMinutes: null,
        nextReview: new Date().toISOString(),
        is_mastered: false,
        orientationCompleted: false
      };
      const batch = writeBatch(db);
      batch.set(progressRef, resetPayload);
      batch.set(
        doc(db, `users/${this.userId}/decks/${deckId}/cards/${cardId}`),
        { is_mastered: false },
        { merge: true }
      );
      await batch.commit();
    } catch (error) {
      console.error('Error resetting card progress:', error);
      throw error;
    }
  }

  async resetSnippetProgress(_snippetId) {}

  /** Legacy — snippet_progress collection is unused. */
  async makeAllSnippetsDue() {
    return 0;
  }

  // Make all cards due for review (for debugging)
  async makeAllCardsDue() {
    try {
      this.debug('Making all cards due for review');
      const progressRef = collection(db, `users/${this.userId}/progress`);
      const snapshot = await getDocs(progressRef);

      this.debug(`Found ${snapshot.docs.length} card progress records`);

      // Set all nextReview dates to yesterday
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      const yesterdayStr = yesterday.toISOString();

      let updateCount = 0;

      // Update each progress record
      await Promise.all(
        snapshot.docs.map(async (docSnapshot) => {
          const progressRef = doc(db, `users/${this.userId}/progress/${docSnapshot.id}`);
          const data = docSnapshot.data();

          this.debug(`Setting nextReview to yesterday for card progress: ${docSnapshot.id}`);

          await updateDoc(progressRef, {
            nextReview: yesterdayStr
          });

          updateCount++;
        })
      );

      this.debug(`Successfully updated ${updateCount} card progress records`);
      return updateCount;
    } catch (error) {
      console.error('Error making cards due:', error);
      throw error;
    }
  }

  // Get decks with due cards
  async getDueDecks() {
    try {
      this.debug('Getting due decks for user:', this.userId);

      const [decksSnapshot, progressRows] = await Promise.all([
        getDocs(collection(db, `users/${this.userId}/decks`)),
        this.fetchAllCardProgressRows()
      ]);
      const decks = decksSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data()
      }));

      const filteredDecks = this.buildDueDecksFromProgress(decks, progressRows);
      this.debug('Final due decks:', filteredDecks.length);
      return filteredDecks;
    } catch (error) {
      console.error('Error getting decks with due cards:', error);
      throw error;
    }
  }

  // Get deck statistics
  async getDeckStats(deckId) {
    try {
      const progressRef = collection(db, `users/${this.userId}/progress`);
      const q = query(progressRef, where('deckId', '==', deckId));
      const snapshot = await getDocs(q);

      let totalCards = 0;
      let totalMastery = 0;
      let totalReviews = 0;
      let totalSuccessful = 0;
      let masteryLevels = {
        [MASTERY_LEVELS.BEGINNER.name]: 0,
        [MASTERY_LEVELS.INTERMEDIATE.name]: 0,
        [MASTERY_LEVELS.ADVANCED.name]: 0,
        [MASTERY_LEVELS.MASTER.name]: 0
      };

      snapshot.forEach(doc => {
        const data = doc.data();
        totalCards++;
        totalMastery += data.mastery || BASE_MASTERY.INITIAL;
        totalReviews += data.totalReviews || 0;
        totalSuccessful += data.successfulReviews || 0;

        const masteryLevel = this.getMasteryLevel(data.mastery || BASE_MASTERY.INITIAL);
        masteryLevels[masteryLevel]++;
      });

      return {
        totalCards,
        averageMastery: totalCards > 0 ? Math.round(totalMastery / totalCards) : 0,
        successRate: totalReviews > 0 ? Math.round((totalSuccessful / totalReviews) * 100) : 0,
        masteryLevels,
        totalReviews
      };
    } catch (error) {
      console.error('Error getting deck stats:', error);
      throw error;
    }
  }
}

export default SpacedRepetition;
